/* Channel Canna: TV hero (sections/channel-tv.liquid). Plain JS, no deps.
 *
 * The TV is on at CH1 on load. Channel changes come from the dial button,
 * the channel tabs (tablist, roving tabindex), arrow keys on the dial/tabs/
 * screen, and a horizontal swipe on the set.
 *
 * Grammar "tune, don't drop" (740ms): 180ms collapse -> 240ms snow (sprite,
 * CSS steps) -> 320ms bloom + print resolve. Dial overshoots 3deg and
 * settles; antennas sway 0.6deg once. Reduced motion or
 * html[data-static="off"]: instant cut with one static frame, no idle motion.
 * Requests during a change are coalesced (the newest wins), so the screen
 * never changes more than ~1.4 times a second (well under 3 flashes/s).
 *
 * API: element.channelTV = { go(n), next(), prev(), get current(), get count() }
 *      window.ChannelTV.get(sectionEl | id) -> that object
 *      event 'channel-tv:change' on the section, detail { channel, title }
 */
(function () {
  'use strict';

  var T_COLLAPSE = 180, T_SNOW = 240, T_RESOLVE = 320;
  var T_FRAME = 90;          // the single static frame in reduced/static-off mode
  var MIN_CUT_GAP = 400;     // reduced mode: at most 2.5 cuts a second
  var sprites = null;        // shared sprite promise (loaded once per page)
  var instances = [];

  var html = document.documentElement;
  var mqReduce = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : { matches: false };

  function motionAllowed() {
    return !mqReduce.matches && html.getAttribute('data-static') !== 'off';
  }

  function loadImage(url) {
    return new Promise(function (resolve, reject) {
      if (!url) { reject(); return; }
      var img = new Image();
      img.decoding = 'async';
      img.onload = function () { (img.decode ? img.decode() : Promise.resolve()).then(function () { resolve(url); }, function () { resolve(url); }); };
      img.onerror = reject;
      try { url = new URL(url, document.baseURI).href; } catch (e) { /* keep as given */ }
      img.src = url;
    });
  }

  function loadSprites(root) {
    if (sprites) return sprites;
    var snow = root.getAttribute('data-snow'), res = root.getAttribute('data-resolve'), pour = root.getAttribute('data-pour');
    sprites = Promise.all([
      loadImage(snow).catch(function () { return null; }),
      loadImage(res).catch(function () { return null; }),
      loadImage(pour).catch(function () { return null; })
    ]).then(function (r) {
      instances.forEach(function (inst) { inst.applySprites(r[0], r[1], r[2]); });
      return r;
    });
    return sprites;
  }

  function ChannelTV(root) {
    this.root = root;
    this.panes = Array.prototype.slice.call(root.querySelectorAll('[data-ch-pane]'));
    this.panels = Array.prototype.slice.call(root.querySelectorAll('[data-ch-panel]'));
    this.tabs = Array.prototype.slice.call(root.querySelectorAll('[data-ch-tab]'));
    this.tablist = root.querySelector('[data-tv-tabs]');
    this.screen = root.querySelector('[data-tv-screen]');
    this.picture = root.querySelector('[data-tv-picture]');
    this.osd = root.querySelector('[data-tv-osd]');
    this.live = root.querySelector('[data-tv-live]');
    this.dialBtn = root.querySelector('[data-tv-dial-button]');
    this.tv = root.querySelector('[data-tv]');
    this.dials = Array.prototype.slice.call(root.querySelectorAll('[data-tv-dial]'));
    this.ants = Array.prototype.slice.call(root.querySelectorAll('.tv-ant'));
    this.count = this.panes.length;
    this.current = 1;
    this.target = 1;
    this.busy = false;
    this.lastCut = 0;
    this.dialDeg = 0;
    this.timers = [];
    if (this.count) this.init();
  }

  ChannelTV.prototype.init = function () {
    var self = this, root = this.root;
    root.classList.add('is-js');
    this.panels.forEach(function (p, i) { p.hidden = false; p.classList.toggle('is-on', i === 0); });
    if (this.count > 1) {
      if (this.tablist) this.tablist.hidden = false;
      if (this.dialBtn) this.dialBtn.hidden = false;
    }
    this.syncMode();
    this.updateLabels(1);

    // tabs: click + roving tabindex (automatic activation)
    this.tabs.forEach(function (tab) {
      tab.addEventListener('click', function () { self.go(+tab.getAttribute('data-ch-tab')); });
    });
    if (this.tablist) {
      this.tablist.addEventListener('keydown', function (e) {
        var n = null;
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') n = self.wrap(self.target + 1);
        else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') n = self.wrap(self.target - 1);
        else if (e.key === 'Home') n = 1;
        else if (e.key === 'End') n = self.count;
        if (n === null) return;
        e.preventDefault();
        self.go(n);
        var t = self.tabs[n - 1];
        if (t) t.focus();
      });
    }

    // dial: click = next channel; arrows turn it either way
    if (this.dialBtn) {
      this.dialBtn.addEventListener('click', function () { self.next(); });
      this.dialBtn.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowRight' || e.key === 'ArrowUp') { e.preventDefault(); self.next(); }
        else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') { e.preventDefault(); self.prev(); }
      });
    }

    // swipe on the set (horizontal only; vertical scroll is left alone)
    var sx = 0, sy = 0, tracking = false;
    this.tv.addEventListener('pointerdown', function (e) {
      if (e.pointerType === 'mouse') return;
      tracking = true; sx = e.clientX; sy = e.clientY;
    }, { passive: true });
    this.tv.addEventListener('pointerup', function (e) {
      if (!tracking) return;
      tracking = false;
      var dx = e.clientX - sx, dy = e.clientY - sy;
      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.4) { if (dx < 0) self.next(); else self.prev(); }
    }, { passive: true });
    this.tv.addEventListener('pointercancel', function () { tracking = false; }, { passive: true });

    // sprites: first interaction, or when the browser is idle
    var kick = function () { loadSprites(root); };
    ['pointerdown', 'keydown', 'focusin', 'touchstart'].forEach(function (ev) {
      root.addEventListener(ev, kick, { once: true, passive: true });
    });
    var idle = function () { loadSprites(root); };
    var onLoad = function () {
      if ('requestIdleCallback' in window) window.requestIdleCallback(idle, { timeout: 4000 });
      else setTimeout(idle, 2500);
    };
    if (document.readyState === 'complete') onLoad(); else window.addEventListener('load', onLoad, { once: true });
    if (sprites) sprites.then(function (r) { self.applySprites(r[0], r[1], r[2]); });

    // pause idle motion off screen / in hidden tabs
    if ('IntersectionObserver' in window) {
      this.io = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) { root.classList.toggle('is-visible', en.isIntersecting && !document.hidden); });
      }, { threshold: 0.05 });
      this.io.observe(root);
    } else {
      root.classList.add('is-visible');
    }
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) root.classList.remove('is-visible');
      else if (!self.io) root.classList.add('is-visible');
      else { self.io.unobserve(root); self.io.observe(root); }
    });
  };

  ChannelTV.prototype.applySprites = function (snow, res, pour) {
    var r = this.root;
    if (snow && res) {
      r.style.setProperty('--ch-snow-img', 'url("' + snow + '")');
      r.style.setProperty('--ch-resolve-img', 'url("' + res + '")');
      r.classList.add('has-sprites');
    }
    if (pour) {
      r.style.setProperty('--ch-pour-img', 'url("' + pour + '")');
      r.classList.add('has-pour');
    }
  };

  ChannelTV.prototype.syncMode = function () {
    this.root.classList.toggle('is-motion', motionAllowed());
  };

  ChannelTV.prototype.wrap = function (n) {
    return ((n - 1 + this.count) % this.count) + 1;
  };
  ChannelTV.prototype.next = function () { this.go(this.wrap(this.target + 1), 1); };
  ChannelTV.prototype.prev = function () { this.go(this.wrap(this.target - 1), -1); };

  ChannelTV.prototype.updateLabels = function (n) {
    var self = this;
    this.tabs.forEach(function (t, i) {
      var on = i === n - 1;
      t.setAttribute('aria-selected', on ? 'true' : 'false');
      t.tabIndex = on ? 0 : -1;
    });
    var off = parseInt((this.root.querySelector('[data-ch-off]') || {}).getAttribute ? this.root.querySelector('[data-ch-off]').getAttribute('data-ch-off') : 0, 10) || 0;
    var shown = n - off;
    if (this.osd) this.osd.textContent = shown > 0 ? 'CH ' + (shown < 10 ? '0' : '') + shown : '';
    if (this.dialBtn) {
      var label = this.dialBtn.querySelector('.ch-sr');
      if (label) {
        if (!this.dialBase) this.dialBase = label.textContent;
        label.textContent = this.dialBase + ' (channel ' + n + ' of ' + self.count + ')';
      }
    }
  };

  ChannelTV.prototype.titleOf = function (n) {
    var p = this.panels[n - 1], h = p && p.querySelector('.ch-panel__title');
    return h ? h.textContent.trim() : '';
  };

  // turn the dial by the shortest way in the direction of travel
  ChannelTV.prototype.turnDial = function (from, to, dir, animate) {
    var steps = to - from;
    if (dir === 1 && steps < 0) steps += this.count;
    else if (dir === -1 && steps > 0) steps -= this.count;
    else if (!dir) { if (steps > this.count / 2) steps -= this.count; if (steps < -this.count / 2) steps += this.count; }
    var start = this.dialDeg, end = start + steps * (360 / Math.max(this.count || 6, 1));
    this.dialDeg = end;
    var d = steps > 0 ? 1 : -1;
    this.dials.forEach(function (g) {
      g.style.setProperty('--ch-dial', end + 'deg');
      if (animate && g.animate) {
        g.animate([
          { transform: 'rotate(' + start + 'deg)' },
          { transform: 'rotate(' + (end + 3 * d) + 'deg)', offset: 0.72 },
          { transform: 'rotate(' + end + 'deg)' }
        ], { duration: 380, easing: 'cubic-bezier(.3,.7,.3,1)' });
      }
    });
    if (animate) {
      this.ants.forEach(function (a, i) {
        if (!a.animate) return;
        var s = i % 2 ? -1 : 1;
        a.animate([
          { transform: 'rotate(0deg)' },
          { transform: 'rotate(' + (0.6 * s) + 'deg)', offset: 0.35 },
          { transform: 'rotate(' + (-0.25 * s) + 'deg)', offset: 0.7 },
          { transform: 'rotate(0deg)' }
        ], { duration: 640, easing: 'ease-out' });
      });
    }
  };

  // Live plates: each story channel can carry a muted ambient loop
  // (<video data-ch-video data-src>). Only the channel on screen loads and
  // plays; reduced motion or Static off keeps the still poster.
  ChannelTV.prototype.syncVideos = function () {
    var still = (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) ||
      document.documentElement.getAttribute('data-static') === 'off';
    var cur = this.current || 1;
    this.panes.forEach(function (p, i) {
      var v = p.querySelector('video[data-ch-video]');
      if (!v) return;
      if (i === cur - 1 && !still) {
        if (!v.getAttribute('src') && v.dataset.src) { v.src = v.dataset.src; v.addEventListener('playing', function () { v.classList.add('is-playing'); }, { once: true }); }
        var pr = v.play(); if (pr && pr.catch) pr.catch(function () {});
      } else if (!v.paused) { v.pause(); }
    });
  };

  ChannelTV.prototype.swap = function (n) {
    var self = this;
    this.panes.forEach(function (p, i) {
      var on = i === n - 1;
      p.hidden = !on;
      p.classList.toggle('is-on', on);
    });
    this.panels.forEach(function (p, i) { p.classList.toggle('is-on', i === n - 1); });
    this.current = n;
    var inner = this.root.querySelector('[data-ch-cur]'); if (inner) inner.setAttribute('data-ch-cur', String(n));
    this.syncVideos();
    if (this.live) this.live.textContent = 'Channel ' + n + (this.titleOf(n) ? ': ' + this.titleOf(n) : '');
    this.root.dispatchEvent(new CustomEvent('channel-tv:change', { bubbles: true, detail: { channel: n, title: self.titleOf(n) } }));
  };

  ChannelTV.prototype.later = function (fn, ms) {
    var id = setTimeout(fn, ms);
    this.timers.push(id);
  };

  ChannelTV.prototype.go = function (n, dir, opts) {
    n = this.wrap(Math.round(n) || 1);
    var instant = !!(opts && opts.instant);
    if (n === this.target) return;
    var from = this.target;
    this.target = n;
    this.updateLabels(n);
    this.syncMode();
    this.turnDial(from, n, dir || 0, motionAllowed() && !instant);
    if (this.busy) return; // the running change picks up the newest target
    loadSprites(this.root);
    this.run(instant);
  };

  ChannelTV.prototype.run = function (instant) {
    if (motionAllowed() && !instant) this.tune(); else this.cut(instant);
  };

  ChannelTV.prototype.done = function () {
    this.busy = false;
    if (this.target !== this.current) this.run(false);
  };

  // reduced motion / Static off: instant cut with one static frame
  ChannelTV.prototype.cut = function (silent) {
    var self = this, now = Date.now(), wait = Math.max(0, MIN_CUT_GAP - (now - this.lastCut));
    this.busy = true;
    this.later(function () {
      self.lastCut = Date.now();
      self.swap(self.target);
      if (silent) { self.done(); return; }
      self.screen.classList.add('is-frame');
      self.later(function () {
        self.screen.classList.remove('is-frame');
        self.done();
      }, T_FRAME);
    }, wait);
  };

  ChannelTV.prototype.tune = function () {
    var self = this, scr = this.screen, pic = this.picture;
    this.busy = true;
    // 1. collapse (180ms): the old picture folds to a line
    var col = pic.animate ? pic.animate([
      { transform: 'scale(1, 1)', filter: 'brightness(1)' },
      { transform: 'scale(1.02, 0.02)', filter: 'brightness(1.08)' }
    ], { duration: T_COLLAPSE, easing: 'cubic-bezier(.6,0,.9,.5)', fill: 'forwards' }) : null;

    this.later(function () {
      // 2. snow (240ms); the new channel is swapped in underneath
      scr.classList.add('is-snow');
      self.swap(self.target);
      if (col) col.cancel();
      pic.style.visibility = 'hidden';
      self.later(function () {
        // 3. bloom + print resolve (320ms)
        pic.style.visibility = '';
        scr.classList.remove('is-snow');
        scr.classList.add('is-resolve');
        if (pic.animate) pic.animate([
          { transform: 'scale(1.025)', filter: 'brightness(1.06)' },
          { transform: 'scale(1)', filter: 'brightness(1)' }
        ], { duration: T_RESOLVE, easing: 'cubic-bezier(.2,.7,.2,1)' });
        self.later(function () {
          scr.classList.remove('is-resolve');
          self.done();
        }, T_RESOLVE);
      }, T_SNOW);
    }, T_COLLAPSE);
  };

  ChannelTV.prototype.destroy = function () {
    this.timers.forEach(clearTimeout);
    if (this.io) this.io.disconnect();
    instances = instances.filter(function (i) { return i !== this; }, this);
  };

  function mount(root) {
    if (!root || root.channelTV) return root && root.channelTV;
    var inst = new ChannelTV(root);
    instances.push(inst);
    // start CH1's live plate once the page is idle (never blocks LCP)
    var kick = function () { inst.syncVideos(); };
    if ('requestIdleCallback' in window) requestIdleCallback(kick, { timeout: 2500 }); else setTimeout(kick, 1200);
    document.addEventListener('cc:static', kick);
    var api = {
      go: function (n) { inst.go(n); },
      next: function () { inst.next(); },
      prev: function () { inst.prev(); },
      get current() { return inst.current; },
      get count() { return inst.count; },
      _inst: inst
    };
    root.channelTV = api;
    return api;
  }

  function mountAll(scope) {
    Array.prototype.forEach.call((scope || document).querySelectorAll('[data-channel-tv]'), mount);
  }

  // live mode changes: OS setting or the header Static/Clear toggle
  function resync() { instances.forEach(function (i) { i.syncMode(); }); }
  if (mqReduce.addEventListener) mqReduce.addEventListener('change', resync);
  else if (mqReduce.addListener) mqReduce.addListener(resync);
  if ('MutationObserver' in window) {
    new MutationObserver(resync).observe(html, { attributes: true, attributeFilter: ['data-static'] });
  }

  window.ChannelTV = {
    get: function (el) {
      if (typeof el === 'string') el = document.getElementById(el);
      return el && el.channelTV;
    },
    mountAll: mountAll
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { mountAll(); });
  else mountAll();

  // theme editor
  document.addEventListener('shopify:section:load', function (e) { mountAll(e.target); });
  document.addEventListener('shopify:section:unload', function (e) {
    var r = e.target.querySelector('[data-channel-tv]');
    if (r && r.channelTV) { r.channelTV._inst.destroy(); r.channelTV = null; }
  });
  document.addEventListener('shopify:block:select', function (e) {
    var panel = e.target.closest ? e.target.closest('[data-ch-panel]') : null;
    var r = panel && panel.closest('[data-channel-tv]');
    if (r && r.channelTV) r.channelTV._inst.go(+panel.getAttribute('data-ch-panel'), 0, { instant: true });
  });
})();
